import { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useWalletElementContext } from '../views/WalletView';
import { useActiveCompanyKey } from '@lendica/hooks';
import { currencyFormatter, Approval, Company } from '@lendica/utils';
import { UsageChart, PayLaterLogo, FundNowLogo, DrawDownLogo } from '@lendica/components';
import { keyframes } from '@emotion/react';
import ProgressCard from './ProgressCard';
import { useWalletApi } from '../walletAPI';

const gradient = keyframes`
	0% {
		background-position: 5% 5%;
	}
	12.5% {
		background-position: 50% 0%;
	}
	25% {
		background-position: 95% 5%;
	}
    37.5% {
		background-position: 100% 50%;
	}
    50% {
        background-position: 95% 95%;
    }
    62.5% {
        background-position: 50% 100%;
    }
    75% {
        background-position: 5% 95%;
    }
    87.5% {
        background-position: 0% 50%;
    }
    100% {
		background-position: 5% 5%;
	}
`;

export const MyCard = () => {
    const api = useWalletApi();
    const { walletRef } = useWalletElementContext();
    const companyKey = useActiveCompanyKey();

    const [company, setCompany] = useState<Company | null>(null);
    const [approvals, setApprovals] = useState<Approval[] | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [companyDetail, approvalsResp] = await Promise.all([
                    api.getCompanyDetail(),
                    api.getApprovals(),
                ]);
                if (cancelled) return;
                setCompany(companyDetail || null);
                setApprovals(approvalsResp?.results ?? []);
            } catch (_e) {
                if (cancelled) return;
                setCompany(null);
                setApprovals([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [companyKey]);

    const applicationStatus = company?.status;

    // TODO: use filters on api instead of filtering here
    const paylaterApproval = useMemo(
        () => approvals?.find(approval => approval.product_name === 'paylater' && approval.active),
        [approvals]
    );
    const drawdownApproval = useMemo(
        () => approvals?.find(approval => approval.product_name === 'drawdown' && approval.active),
        [approvals]
    );
    const fundnowApproval = useMemo(
        () => approvals?.find(approval => approval.product_name === 'fundnow' && approval.active),
        [approvals]
    );

    const {
        paylater_total_payoff_amount,
        paylater_funds_available,
        paylater_qualified_amount,
        drawdown_total_payoff_amount,
        drawdown_funds_available,
        drawdown_qualified_amount,
        fundnow_total_payoff_amount,
        fundnow_funds_available,
        fundnow_qualified_amount,
        balanceTotal,
        availableCreditTotal,
        qualifiedAmountTotal,
    } = useMemo(() => {
        const p_total = paylaterApproval?.total_payoff_amount ?? 0;
        const p_avail = paylaterApproval?.funds_available ?? 0;
        const p_qual = paylaterApproval?.qualified_amount ?? 0;

        const d_total = drawdownApproval?.total_payoff_amount ?? 0;
        const d_avail = drawdownApproval?.funds_available ?? 0;
        const d_qual = drawdownApproval?.qualified_amount ?? 0;

        const f_total = fundnowApproval?.total_payoff_amount ?? 0;
        const f_avail = fundnowApproval?.funds_available ?? 0;
        const f_qual = fundnowApproval?.qualified_amount ?? 0;

        return {
            paylater_total_payoff_amount: p_total,
            paylater_funds_available: p_avail,
            paylater_qualified_amount: p_qual,
            drawdown_total_payoff_amount: d_total,
            drawdown_funds_available: d_avail,
            drawdown_qualified_amount: d_qual,
            fundnow_total_payoff_amount: f_total,
            fundnow_funds_available: f_avail,
            fundnow_qualified_amount: f_qual,
            balanceTotal: p_total + d_total + f_total,
            availableCreditTotal: p_avail + d_avail + f_avail,
            qualifiedAmountTotal: p_qual + d_qual + f_qual,
        };
    }, [paylaterApproval, drawdownApproval, fundnowApproval]);

    return (
        <Box pt={2} ref={walletRef}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h5">Wallet</Typography>
            </Box>

            <Box
                sx={{
                    mt: 2,
                    p: 3,
                    minHeight: '80px',
                    borderRadius: 3,
                    color: 'primary.contrastText',
                    background:
                        'radial-gradient(ellipse at top, #4a4a68, transparent), radial-gradient(ellipse at left, #ee7752, transparent), radial-gradient(ellipse at bottom, #23d5ab, transparent), radial-gradient(ellipse at right, #23a6d5, transparent)',
                    backgroundSize: '145% 185%',
                    animation: `${gradient} 24s linear infinite`,
                    // boxShadow: theme => theme.shadows[1],
                    boxShadow: `0 1px 1px rgba(96, 96, 96, 0.11), 
                                0 2px 2px rgba(96, 96, 96, 0.11), 
                                0 4px 4px rgba(96, 96, 96, 0.11), 
                                0 6px 8px rgba(96, 96, 96, 0.11),
                                0 8px 16px rgba(96, 96, 96, 0.11)`,
                }}
            >
                <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    <Typography variant="h5" fontWeight={400}>
                        {(company?.company_name || (company as any)?.company_legal_name) ?? ''}
                    </Typography>
                    <Box sx={{ m: -1, width: '116px', height: '116px' }}>
                        <UsageChart
                            total={qualifiedAmountTotal}
                            available={availableCreditTotal}
                            color="#FCFCFDE6"
                            bgColor="#3B3B53E6"
                        />
                    </Box>
                </Box>

                <Box mt={4} display="flex" flexDirection="row" justifyContent="space-between">
                    <Box>
                        <Typography variant="body2">Balance</Typography>
                        <Typography variant="h6" fontWeight={800} data-testid="total-balance">
                            {applicationStatus === 1 ? 'Pending' : currencyFormatter(balanceTotal)}
                        </Typography>
                    </Box>
                    <Box display="flex" flexDirection="column" alignItems="flex-end">
                        <Typography variant="body2">Credit Available</Typography>
                        <Typography variant="h6" fontWeight={800} data-testid="credit-available">
                            {applicationStatus === 1
                                ? 'Pending'
                                : currencyFormatter(availableCreditTotal)}
                        </Typography>
                        <Typography variant="caption" color={'#FFF'} data-testid="credit-line">
                            of {currencyFormatter(qualifiedAmountTotal, true)} credit line
                        </Typography>
                    </Box>
                </Box>
            </Box>

            <Box mt={2.5}>
                {paylaterApproval && (
                    <ProgressCard
                        product={<PayLaterLogo height={20} />}
                        total_payoff_amount={paylater_total_payoff_amount}
                        funds_available={paylater_funds_available}
                        qualified_amount={paylater_qualified_amount}
                    />
                )}

                {drawdownApproval && (
                    <ProgressCard
                        product={<DrawDownLogo height={16} />}
                        total_payoff_amount={drawdown_total_payoff_amount}
                        funds_available={drawdown_funds_available}
                        qualified_amount={drawdown_qualified_amount}
                    />
                )}

                {fundnowApproval && (
                    <ProgressCard
                        product={<FundNowLogo height={17} />}
                        total_payoff_amount={fundnow_total_payoff_amount}
                        funds_available={fundnow_funds_available}
                        qualified_amount={fundnow_qualified_amount}
                    />
                )}
            </Box>
        </Box>
    );
};
